<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin workflow-editor">
      <q-card-section class="row">
        <div
          v-if="!userCanEditWorkflow"
          class="text-h6"
        >
          {{ $t('programs.workflows.editWorkflow.textTitlePreview') }}
        </div>
        <div
          v-else-if="extendWorkflow"
          class="text-h6"
        >
          {{ $t('programs.workflows.editWorkflow.textTitleAdjustSettings') }}
        </div>
        <div
          v-else-if="hasWorkflowId"
          class="text-h6"
        >
          {{ $t('programs.workflows.editWorkflow.textTitleEdit') }}
        </div>
        <div
          v-else
          class="text-h6"
        >
          {{ $t('programs.workflows.editWorkflow.textTitleAdd') }}
        </div>
        <q-space />
        <q-btn
          flat
          round
          dense
          icon="help_outline"
          @click="onShowHelp"
        />
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-form
        greedy
        @submit="onSaveWorkflow"
      >
        <q-card-section class="q-gutter-md q-pt-none">
          <div class="col-12 text-overline q-mt-none q-pt-none text-grey-5 text-uppercase">
            {{ workflowTypeLabel }}
          </div>
          <div class="col-12">
            <info
              v-if="extendWorkflow"
            >
              {{ $t('programs.workflows.editWorkflow.textAdjustWorkflow', {extendOrigin }) }}
            </info>
          </div>
          <edit-workflow-details
            ref="status_probe"
            v-model="editWorkflow"
            :original-workflow="workflow"
            status="probe"
            :workflow-statuses="editWorkflowStatuses"
            :user-can-edit-workflow="userCanEditWorkflow"
            :extend-workflow="extendWorkflow"
            :has-min-number-of-trials="hasMinNumberOfTrials"
            :only-status="isManualWorkflow"
            @edit-status="onEditStatus"
            @restore-status="onRestoreStatus"
          />
          <transition
            v-for="status in acquisitionStatuses"
            :key="status"
            appear
            enter-active-class="animated zoomIn"
          >
            <edit-workflow-details
              v-model="editWorkflow"
              :original-workflow="workflow"
              :status="status"
              :workflow-statuses="editWorkflowStatuses"
              :user-can-edit-workflow="userCanEditWorkflow"
              :extend-workflow="extendWorkflow"
              :has-min-number-of-trials="hasMinNumberOfTrials"
              :only-status="isManualWorkflow"
              @edit-status="onEditStatus"
              @restore-status="onRestoreStatus"
            />
          </transition>
          <q-card
            v-if="userCanEditWorkflow && userCanExtendParameter"
            flat
            bordered
            class="col-12"
          >
            <q-card-section
              class="q-py-sm bg-grey-2"
            >
              <q-btn
                :label="$t('programs.workflows.editWorkflow.textAddStatus')"
                icon="add"
                color="dark"
                flat
                class="full-width"
                @click="onAddStatus('acquisition')"
              />
            </q-card-section>
          </q-card>
          <edit-workflow-details
            v-model="editWorkflow"
            :original-workflow="workflow"
            status="mastered"
            :workflow-statuses="editWorkflowStatuses"
            :user-can-edit-workflow="userCanEditWorkflow"
            :extend-workflow="extendWorkflow"
            :has-min-number-of-trials="hasMinNumberOfTrials"
            :only-status="isManualWorkflow"
            @edit-status="onEditStatus"
            @restore-status="onRestoreStatus"
          />
          <q-card
            v-if="!isManualWorkflow"
            flat
            bordered
            class="col-12"
          >
            <q-card-section class="q-py-sm bg-grey-2">
              <div>
                <status-chip
                  target-status="waiting"
                  size="md"
                  :statuses="editWorkflowStatuses"
                />
              </div>
            </q-card-section>
            <q-separator />
            <q-card-section class="">
              <q-checkbox
                v-model="autoOpenTargets"
                :label="$t('programs.workflows.workflowDetails.inputAutoOpenTargetsLabel')"
                :disable="!userCanEditWorkflow"
              />
              <template
                v-if="autoOpenTargets"
              >
                <q-input
                  v-model.number="autoOpenTargetsCount"
                  type="number"
                  :label="$t('programs.workflows.workflowDetails.inputAutoOpenNumTargetsLabel')"
                  :rules="rules.targetsCount"
                  :suffix="$t('programs.workflows.workflowDetails.suffixTargets')"
                  min="1"
                  :readonly="!userCanEditWorkflow"
                  lazy-rules
                  :input-class="'text-right ' + isValueAdjusted('waiting', 'autoOpenTargetsCount')"
                  class="col-12"
                />
                <q-select
                  v-model="autoOpenTargetsToStatus"
                  :options="initialTargetStatuses"
                  :readonly="!userCanEditWorkflow"
                  :label="$t('programs.workflows.workflowDetails.selectAutoOpenTargetsToStatusLabel')"
                  map-options
                  emit-value
                  :rules="rules.targetsToStatus"
                  class="col-12"
                >
                  <template #option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                    >
                      <q-item-section avatar>
                        <status-chip
                          :target-status="scope.opt.value"
                          :statuses="editWorkflowStatuses"
                        />
                      </q-item-section>
                    </q-item>
                  </template>
                  <template #selected-item="scope">
                    <q-icon
                      v-if="isValueAdjusted('waiting', 'autoOpenTargetsToStatus')"
                      name="warning"
                      color="red"
                    >
                      <q-tooltip>{{ $t('programs.workflows.workflowDetails.textChanged') }}</q-tooltip>
                    </q-icon>
                    <status-chip
                      :target-status="scope.opt.value"
                      :statuses="editWorkflowStatuses"
                    />
                  </template>
                </q-select>
                <q-select
                  v-model="autoOpenDirection"
                  :options="[
                    {value: 'forward',label:$t('programs.workflows.workflowDetails.autoOpenDirections.forward')},
                    {value: 'backward',label:$t('programs.workflows.workflowDetails.autoOpenDirections.backward')},
                    {value: 'total',label:$t('programs.workflows.workflowDetails.autoOpenDirections.total')}
                  ]"
                  :readonly="!userCanEditWorkflow"
                  :label="$t('programs.workflows.workflowDetails.selectAutoOpenDirectionLabel')"
                  :hint="$t('programs.workflows.workflowDetails.selectAutoOpenDirectionHint')"
                  map-options
                  emit-value
                  class="col-12"
                >
                  <template #selected-item="scope">
                    <q-icon
                      v-if="isValueAdjusted('waiting', 'autoOpenDirection')"
                      name="warning"
                      color="red"
                      class="q-pr-sm"
                    >
                      <q-tooltip>{{ $t('programs.workflows.workflowDetails.textChanged') }}</q-tooltip>
                    </q-icon>
                    {{ scope.opt.label }}
                  </template>
                </q-select>
              </template>
            </q-card-section>
          </q-card>
        </q-card-section>
        <q-card-section class="q-pt-none">
          <q-input
            ref="workflowName"
            v-model="workflowName"
            :readonly="!userCanEditWorkflow"
            :disable="!userCanExtendParameter"
            outlined
            class="col-12"
            :rules="rules.workflowName"
            :label="$t('programs.workflows.editWorkflow.inputWorkflowNameLabel')"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            v-if="!userCanEditWorkflow"
            flat
            color="primary"
            :label="$t('common.buttonClose')"
            @click="onCancelClick"
          />
          <q-btn
            v-if="userCanEditWorkflow"
            flat
            color="secondary"
            :label="$t('common.buttonCancel')"
            @click="onCancelClick"
          />
          <q-btn
            v-if="userCanEditWorkflow"
            type="submit"
            :disable="!networkOnline"
            flat
            :loading="waitingSpinner"
            color="primary"
            data-e2e="button-save"
            :label="$t('common.buttonSave')"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>
<script>
import { measurementMetrics, targetStatuses, workflowTypes } from 'boot/globals';
import Info from 'components/Info';
import StatusChip from 'components/Programs/StatusChip';
import EditWorkflowDetails from 'components/Programs/Workflows/EditWorkflowDetails';
import EditWorkflowStatus from 'components/Programs/Workflows/EditWorkflowStatus';
import {
  chain, cloneDeep, find, get, isEmpty, isEqual, merge, omitBy,
  trim,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { openURL } from 'quasar';
import { notifySuccess, useDialog } from 'src/composables/interactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { workflows } from 'src/store/workflows';

export default {
  name: 'EditWorkflow',

  components: {
    EditWorkflowDetails,
    Info,
    StatusChip,
  },

  mixins: [Toolbox],

  props: {
    workflow: {
      type: Object,
      required: true,
    },
    extendWorkflow: {
      type: Boolean,
      required: false,
      default: false,
    },
    extendWorkflowDetails: {
      type: Object,
      required: false,
      default: null,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    extendOrigin: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { openComponentDialog } = useDialog();
    return { openComponentDialog };
  },

  data() {
    return {
      showMoreProbeSettings: false,

      waitingSpinner: false,
      editWorkflow: {
        id: null,
        version: 2,
        workflowName: null,

        statuses: { },

        isDeleted: false,
      },

      rules: {
        targetsCount: [
          (val) => Number.isInteger(val) || this.$t('common.errors.integerValue'),
          (val) => val > 0 || this.$t('programs.workflows.workflowDetails.inputAutoOpenTargetsValidator'),
        ],
        targetsToStatus: [
          (val) => !!val || this.$t('programs.workflows.workflowDetails.selectAutoOpenTargetsToStatusValidator'),
        ],
        workflowName: [(val) => !!val || this.$t('programs.workflows.editWorkflow.inputWorkflowNameValidator')],
      },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(network, ['networkOnline']),

    userCanEditWorkflow() {
      return (!this.readonly) || this.extendWorkflow;
    }, // userCanEditWorkflow()

    userCanExtendParameter() {
      return !this.readonly && !this.extendWorkflow;
    }, // userCanExtendParameter()

    hasWorkflowId() {
      return (get(this.workflow, 'id', null) !== null);
    }, // hasWorkflowId()

    isManualWorkflow() {
      return this.workflowType === 'manual';
    },

    editWorkflowStatuses() {
      const editWorkflowStatuses = get(this.editWorkflow, 'statuses', {});

      // Merge with empty first param to make sure targetStatuses are not mutated
      return merge({}, targetStatuses, editWorkflowStatuses);
    }, // editWorkflowStatuses()

    acquisitionStatuses() {
      return chain(this.editWorkflowStatuses)
        .filter({ type: 'acquisition' })
        .sortBy('order')
        .map('value')
        .value();
    }, // acquisitionStatuses()

    workflowTypeLabel() {
      return this.$t(
        'programs.workflows.workflowDetails.typeSubHeading',
        { type: get(this.workflowTypeInfo, 'label') },
      );
    }, // workflowTypeLabel()

    hasColdProbeOption() {
      return get(this.workflowTypeInfo, 'hasColdProbeOption', false);
    }, // hasColdProbeOption()

    hasMinNumberOfTrials() {
      return get(this.workflowTypeInfo, 'hasMinNumberOfTrials', false);
    }, // hasMinNumberOfTrials()

    targetStatuses() {
      return targetStatuses;
    }, // targetStatuses()

    probeMasteredStatusTargetStatuses() {
      return chain(this.targetStatuses)
        .pick('mastered', 'closed')
        .values()
        .sortBy('order')
        .value();
    }, // probeMasteredStatusTargetStatuses()

    initialTargetStatuses() {
      return chain(this.editWorkflowStatuses)
        .pick(
          chain(this.editWorkflowStatuses)
            .pickBy({ type: 'acquisition' })
            .keys()
            .concat(['probe'])
            .value(),
        )
        .values()
        .sortBy('order')
        .value();
    }, // initialTargetStatuses()

    autoOpenTargets: {
      get() {
        return get(this.editWorkflow, ['statuses', 'waiting', 'autoOpenTargets']) || false;
      },
      set(autoOpenTargets) {
        this.inputStatusValue('waiting', 'autoOpenTargets', autoOpenTargets);
      },
    }, // autoOpenTargets()

    autoOpenTargetsCount: {
      get() {
        return get(this.editWorkflow, ['statuses', 'waiting', 'autoOpenTargetsCount']);
      },
      set(autoOpenTargetsCount) {
        this.inputStatusValue('waiting', 'autoOpenTargetsCount', autoOpenTargetsCount);
      },
    }, // autoOpenTargetsCount()

    autoOpenTargetsToStatus: {
      get() {
        return get(this.editWorkflow, ['statuses', 'waiting', 'autoOpenTargetsToStatus']);
      },
      set(autoOpenTargetsToStatus) {
        this.inputStatusValue('waiting', 'autoOpenTargetsToStatus', autoOpenTargetsToStatus);
      },

    }, // autoOpenTargetsToStatus()

    autoOpenDirection: {
      get() {
        return get(this.editWorkflow, ['statuses', 'waiting', 'autoOpenDirection']) || 'forward';
      },
      set(autoOpenDirection) {
        this.inputStatusValue('waiting', 'autoOpenDirection', autoOpenDirection);
      },
    }, // autoOpenDirection()

    workflowType() {
      return this.editWorkflow?.type || 'accuracyIncrease';
    }, // workflowType()

    workflowTypeInfo() {
      return workflowTypes[this.workflowType] || null;
    }, // workflowTypeInfo()

    measurementMetric() {
      return measurementMetrics.find((metric) => metric.id === this.workflowTypeInfo?.metric);
    }, // measurementMetric()

    workflowName: {
      get() {
        if (this.editWorkflow.workflowName !== null) {
          return this.editWorkflow.workflowName;
        }
        if (this.isManualWorkflow) {
          return chain(this.editWorkflowStatuses)
            .reject('isDeleted')
            .filter('canCollectData')
            .map('label')
            .join(', ')
            .value();
        }
        const workflowProp = this.workflowTypeInfo?.workflowProp || 'accuracy';
        const count = get(this.editWorkflow, ['statuses', 'acquisition', 'sessions'], null);
        let value;
        if (this.measurementMetric?.measurementMetricGroup === 'duration') {
          value = this.formatDuration(get(this.editWorkflow, ['statuses', 'acquisition', workflowProp]) || 0);
        } else {
          value = get(this.editWorkflow, ['statuses', 'acquisition', workflowProp], '-');
        }
        const workflowTypeLabel = this.workflowTypeInfo?.label;
        const suffix = this.measurementMetric?.suffix ? ` ${this.measurementMetric?.suffix}` : '';
        let retVal = '';
        if (this.workflowType === 'accuracyIncrease') {
          retVal = this.$tc(
            'programs.workflows.editWorkflow.textWorkflowNameTemplateAccuracy',
            count,
            {
              value,
              suffix,
              count,
            },
          );
        } else {
          retVal = this.$tc(
            'programs.workflows.editWorkflow.textWorkflowNameTemplate',
            count,
            {
              workflowTypeLabel,
              value,
              suffix,
              count,
            },
          );
        }
        const minTrials = get(this.editWorkflow, ['statuses', 'acquisition', 'minTrials'], null);
        if (minTrials > 1) {
          retVal += ` ${this.$tc(
            'programs.workflows.editWorkflow.textWorkflowNameTemplateMinTrials',
            minTrials,
            { count: minTrials },
          )}`;
        }

        const autoOpenTargets = get(this.editWorkflow, ['statuses', 'waiting', 'autoOpenTargets'], null);
        const autoOpenTargetsCount = get(this.editWorkflow, ['statuses', 'waiting', 'autoOpenTargetsCount'], null);
        const autoOpenTargetsToStatus = this.getTargetProperty(
          get(this.editWorkflow, ['statuses', 'waiting', 'autoOpenTargetsToStatus']),
          'label',
        );
        if (autoOpenTargets) {
          retVal += this.$tc(
            'programs.workflows.editWorkflow.textWorkflowNameTemplateAutoOpen',
            autoOpenTargetsCount,
            { count: autoOpenTargetsCount, autoOpenTargetsToStatus },
          );
        }
        return retVal;
      },
      set(workflowName) {
        this.editWorkflow.workflowName = workflowName;
      },
    }, // workflowName()

  },

  methods: {
    ...mapActions(workflows, ['createWorkflow', 'updateWorkflow']),

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.initWorkflow();
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    onShowHelp() {
      openURL('https://help.hirasmus.com/knowledge/automatic-mastery-workflows/');
    }, // onShowHelp()

    async onSaveWorkflow() {
      if (this.extendWorkflow) {
        const workflowAdjustments = chain(this.editWorkflow)
          .get('statuses')
          .mapValues((statusInfo, status) => (
            omitBy(
              statusInfo,
              (workflowParam, workflowKey) => (
                isEqual(workflowParam, get(this.workflow, ['statuses', status, workflowKey]))
              ),
            )
          ))
          .omitBy((statusInfo) => isEmpty(statusInfo))
          .value();

        this.$emit('ok', { workflowId: this.workflow.id, workflowAdjustments });
      } else {
        const workflowId = await this.saveWorkflow();
        this.$emit('ok', { workflowId });
      }
      this.hide();
    }, // onSaveWorkflow()

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    }, // onCancelClick()

    onAddStatus(defaultStatus) {
      this.openComponentDialog(
        EditWorkflowStatus,
        {
          statusInfo: get(this.editWorkflowStatuses, defaultStatus),
          isCreating: true,
          editWorkflowStatuses: this.editWorkflowStatuses,
        },
        async ({ label, color, icon }) => {
          const defaultSettings = chain(targetStatuses)
            .get(defaultStatus)
            .pick(['icon', 'color', 'canCollectData'])
            .value();

          const status = this.generateStatusId(label);
          const order = chain(this.editWorkflowStatuses)
            .filter({ type: defaultStatus })
            .maxBy('order')
            .get('order')
            .add(1)
            .value();

          const newStatusObj = {
            ...get(this.getDefaultWorkflowDetails(this.editWorkflow), defaultStatus),
            ...defaultSettings,
            type: defaultStatus,
            value: status,
            label: trim(label),
            order,
            ...(color && { color }),
            ...(icon && { icon }),
          };

          this.editWorkflow.statuses = merge({}, get(this.editWorkflow, 'statuses', {}), { [status]: newStatusObj });

          // @todo Scroll to new element in $nextTick
        },
      );
    }, // onAddStatus()

    onEditStatus(status) {
      this.openComponentDialog(
        EditWorkflowStatus,
        { statusInfo: get(this.editWorkflowStatuses, status) },
        (newStatusObj) => {
          let statuses = {};
          if ((newStatusObj.isDeleted)
            && (isEmpty(find(targetStatuses, { value: status })))) {
            // Delete a custom status
            statuses = chain(this.editWorkflow)
              .get('statuses', {})
              .omit(status)
              .value();
          } else {
            statuses = chain(this.editWorkflow)
              .get('statuses', {})
              .omit(status)
              .set(status, newStatusObj)
              .value();
          }

          this.editWorkflow.statuses = statuses;
          this.$forceUpdate();
        },
      );
    }, // onEditStatus()

    setStatusProperty(status, property, value) {
      const statuses = chain(this.editWorkflow)
        .get('statuses', {})
        .set([status, property], value)
        .value();

      this.editWorkflow.statuses = statuses;
      // @todo Check if we still need to forceUpdate
      this.$forceUpdate();
    }, // setStatusProperty()

    onRestoreStatus(status) {
      this.setStatusProperty(status, 'isDeleted', false);
    }, //  onRestoreStatus()

    getTargetProperty(targetStatus, property, defaultValue = '') {
      return get(this.editWorkflowStatuses, [targetStatus, property], defaultValue);
    }, // getTargetProperty()

    inputStatusValue(status, parameter, value) {
      this.editWorkflow = merge({}, this.editWorkflow, { statuses: { [status]: { [parameter]: value } } });
    }, // inputStatusValue()

    async saveWorkflow() {
      this.waitingSpinner = true;

      this.editWorkflow = {
        ...this.editWorkflow,
        workflowName: this.workflowName,
      };

      if (this.workflow.id) {
        // Update workflow
        await this.updateWorkflow(this.editWorkflow);
        this.waitingSpinner = false;
        return this.workflow.id;
      }

      // Create new workflow
      const { id } = await this.createWorkflow(this.editWorkflow);
      this.waitingSpinner = false;
      notifySuccess(this.$t('programs.workflows.editWorkflow.textCreatedSuccessfully'));

      return id;
    }, // saveWorkflow()

    isValueAdjusted(status, param) {
      if (!this.extendWorkflow) {
        return '';
      }
      if (!chain(this.editWorkflow)
        .get(['statuses', status, param])
        .isEqual(get(this.workflow, ['statuses', status, param]))
        .value()) {
        return 'text-bold text-red';
      }
      return '';
    }, // isValueAdjusted()

    getDefaultWorkflowDetails(type) {
      return get(workflowTypes, [type, 'defaultWorkflowDetails']);
    }, // getDefaultWorkflowDetails()

    generateStatusId(statusLabel) {
      return chain(statusLabel)
        .trim()
        .toLower()
        .replace(/[^a-z0-9]/gi, '')
        .value();
    }, // generateStatusId()

    initWorkflow() {
      if (get(this.workflow, 'id', null) === null) {
        this.editWorkflow = {
          id: null,
          version: 2,
          clinic: this.userDetails.clinic,
          workflowName: null,

          statuses: {
            ...this.getDefaultWorkflowDetails(
              get(this.workflow, 'type', 'accuracyIncrease'),
            ),
          },
          isDeleted: false,
          ...this.workflow,
        };
      } else {
        this.editWorkflow = cloneDeep(this.workflow);

        // @todo Convert to new version
        this.editWorkflow.version = 2;

        // ---
        if (!isEmpty(this.extendWorkflowDetails)) {
          this.editWorkflow.statuses = merge(
            {},
            get(this.editWorkflow, 'statuses'),
            this.extendWorkflowDetails,
          );
        }
      }
    },

  },

};
</script>
<style scoped>
.workflow-editor {
  min-width:75vw;
}
</style>
